import { useSearchParams } from 'react-router';
import { useTracker } from '@archipro-website/tracker';
import type { accounts, CredentialResponse } from 'google-one-tap';
import { useFetcher } from 'react-router';
import { useBoolean, useMemoizedFn } from 'ahooks';
import { useUser } from '@modules/user';
import { useCallback, useEffect, useRef } from 'react';
import { useScript } from '~/modules/root/hook/use-script';
import { assertSuccessResponse } from '~/modules/root/graphql/responses';
import { decodeLoginRedirect, isCleanRedirect } from '~/modules/root';
import { trackAuthEvent } from '~/modules/user/util/track-auth-event';
import type { UserOneTapSignInAction } from '~/routes/remix-api.user.one-tap-sign-in';

// Interface for object google injected into page as the 'google' global
interface GoogleShell {
    accounts: accounts;
}

declare const google: GoogleShell | undefined;

/**
 * useOneTapSignIn:
 * @param clientId
 *
 * Note: If the popup does not show it is may be because
 * 1. g_state cookie is set which has an exponential cooldown. Delete the cookie to show the popup
 * 2. The <script/> is loaded using ClientOnly and the onLoad event is not firing properly.
 */
export const useOneTapSignIn = (clientId: string, disable = false): void => {
    const user = useUser();
    const otsFetcher = useFetcher<UserOneTapSignInAction>();
    const tracker = useTracker();

    const status = useScript(`https://accounts.google.com/gsi/client`, {
        removeOnUnmount: false,
        shouldPreventLoad: disable,
    });

    const handleCallback = useMemoizedFn(
        (googleResponse: CredentialResponse) => {
            const { credential } = googleResponse;
            otsFetcher.submit(
                { credential },
                {
                    method: 'post',
                    action: '/remix-api/user/one-tap-sign-in',
                },
            );
        },
    );

    const isLoggedIn = user?.__typename === 'Me';
    const [promptAllowed, setPromptAllowed] = useBoolean(false);
    const [promptLoaded, setPrompt] = useBoolean(false);
    const [searchParams] = useSearchParams();

    /**
     * Side effect:
     * Full page reload after any login to refresh userId's in our GTM and GA4 trackers
     */
    const otsResultLogged = useRef<unknown>(null);
    useEffect(() => {
        if (otsFetcher.state !== 'idle' || !otsFetcher.data) {
            return;
        }
        if (
            assertSuccessResponse<{
                isNewMember?: boolean;
                requiresMfa?: boolean;
            }>(otsFetcher.data)
        ) {
            if (otsResultLogged.current !== otsFetcher.data) {
                otsResultLogged.current = otsFetcher.data;
                const result = otsFetcher.data.data;
                const navigateOn = () => {
                    const urlRedirect = searchParams.has('redirect')
                        ? decodeLoginRedirect(searchParams.get('redirect'))
                        : null;
                    if (urlRedirect && isCleanRedirect(urlRedirect)) {
                        window.location.href = urlRedirect;
                        return;
                    }
                    window.location.reload();
                };
                // The reload wipes client state and the post-auth URL-param
                // flow never runs for OneTap, so these are the only success
                // markers — log both, flush the queues (the 1s queue timer
                // would lose them to the reload), then navigate.
                void (async () => {
                    tracker.log('OneTapAuthSuccess', {
                        url: new URL(window.location.href),
                        data: {
                            authSource: 'onetap',
                            Source: 'onetap',
                            ExtraData: JSON.stringify({
                                authSource: 'onetap',
                            }),
                        },
                    });
                    if (!result?.requiresMfa) {
                        // First-class login-method event: provider lands in
                        // the warehouse EVENT_EXTRA_DATA column.
                        await trackAuthEvent({
                            tracker,
                            event: result?.isNewMember
                                ? 'new_account_registered'
                                : 'user_login',
                            provider: 'GoogleOneTap',
                            authSource: 'onetap',
                        });
                    }
                    tracker.release();
                    navigateOn();
                })();
            }
            return;
        }
        if (otsResultLogged.current !== otsFetcher.data) {
            otsResultLogged.current = otsFetcher.data;
            tracker.log('OneTapAuthError', {
                url: new URL(window.location.href),
                data: {
                    authSource: 'onetap',
                    Source: 'onetap',
                    ExtraData: JSON.stringify({ authSource: 'onetap' }),
                },
            });
        }
    }, [otsFetcher, searchParams, tracker]);

    /**
     * Side effect:
     * Sets promptAllowed to true immediately when script is ready.
     * This allows the prompt to show immediately without waiting for navigation.
     */
    useEffect(() => {
        if (status === 'ready' && !promptAllowed) {
            setPromptAllowed.setTrue();
        }
    }, [status, setPromptAllowed, promptAllowed]);

    const handleShowOTS = useCallback(() => {
        // User has blocked one tap sign in
        if (searchParams.get('blockOneTap') !== null) {
            return;
        }

        if (typeof google === 'undefined') {
            return;
        }

        if (!isLoggedIn && !promptLoaded && promptAllowed) {
            google.accounts.id.initialize({
                client_id: clientId,
                callback: handleCallback,
                use_fedcm_for_prompt: true,
            });

            // FedCM suppresses display moments, so log the request
            // deterministically and the displayed confirmation only when the
            // browser still reports it.
            // Source and ExtraData are the only custom keys the warehouse
            // pipeline persists; the flat keys serve network QA.
            tracker.log('LoginPromptShown', {
                url: new URL(window.location.href),
                data: {
                    authSource: 'onetap',
                    moment: 'requested',
                    Source: 'onetap',
                    ExtraData: JSON.stringify({
                        authSource: 'onetap',
                        moment: 'requested',
                    }),
                },
            });
            google.accounts.id.prompt((notification) => {
                if (notification?.isDisplayed?.()) {
                    tracker.log('LoginPromptShown', {
                        url: new URL(window.location.href),
                        data: {
                            authSource: 'onetap',
                            moment: 'displayed',
                            Source: 'onetap',
                            ExtraData: JSON.stringify({
                                authSource: 'onetap',
                                moment: 'displayed',
                            }),
                        },
                    });
                }
                // 'requested' overcounts impressions when Google suppresses
                // the prompt (dismissal cooldown, opt-out). Log the negative
                // moments too, where the browser still reports them, so the
                // impression denominator can be corrected in analysis.
                if (notification?.isSkippedMoment?.()) {
                    const reason = notification.getSkippedReason?.();
                    tracker.log('LoginPromptShown', {
                        url: new URL(window.location.href),
                        data: {
                            authSource: 'onetap',
                            moment: 'skipped',
                            ...(reason && { reason }),
                            Source: 'onetap',
                            ExtraData: JSON.stringify({
                                authSource: 'onetap',
                                moment: 'skipped',
                                ...(reason && { reason }),
                            }),
                        },
                    });
                }
                if (notification?.isDismissedMoment?.()) {
                    const reason = notification.getDismissedReason?.();
                    tracker.log('LoginPromptShown', {
                        url: new URL(window.location.href),
                        data: {
                            authSource: 'onetap',
                            moment: 'dismissed',
                            ...(reason && { reason }),
                            Source: 'onetap',
                            ExtraData: JSON.stringify({
                                authSource: 'onetap',
                                moment: 'dismissed',
                                ...(reason && { reason }),
                            }),
                        },
                    });
                }
            });
            setPrompt.setTrue();
        } else if (isLoggedIn && promptLoaded) {
            setPrompt.setFalse();
            google.accounts.id.cancel();
        }
    }, [
        clientId,
        handleCallback,
        isLoggedIn,
        promptLoaded,
        searchParams,
        setPrompt,
        promptAllowed,
        tracker,
    ]);

    useEffect(() => {
        if (disable) return;

        if (status === 'ready') {
            handleShowOTS();
        }
    }, [status, user, handleShowOTS, disable]);
};
